/**

  ___                       _      ___       _        
 |   \ _  _ _ _  __ _ _ __ (_)__  |   \ __ _| |_ __ _ 
 | |) | || | ' \/ _` | '  \| / _| | |) / _` |  _/ _` |
 |___/ \_, |_||_\__,_|_|_|_|_\__| |___/\__,_|\__\__,_|
       |__/
       
*/
import { fromLatLngToPoint, VisionApplication } from '../application';
import { RingBuffer } from '../components/ring-buffer';
import { PublishTopic } from '../events';
import { AssetDTO, AssetService, MotionState } from '../service/asset';
import { AssetTelemetry } from '../service/latest';
import { VisionMap } from './map';
import { SVGIcons } from './svg';

export interface VisionMapLocation {
    latitude: number;
    longitude: number;
}

export interface VisionMapMarkerConfig {
    location: VisionMapLocation;
    rotation: number;
    image: string;
    label: string;
    indicator: [boolean, boolean, boolean, boolean];
}

export class VisionMapMarker {
    static FRONT_ARC = 0;
    static TOP_ARC = 1;
    static REAR_ARC = 2;
    static BOTTOM_ARC = 3;

    highlight: boolean;
    marker!: google.maps.Marker;
    clickMarker!: google.maps.Marker;

    context!: CanvasRenderingContext2D;
    width!: number;
    image!: HTMLImageElement;

    offImage!: HTMLImageElement;
    movingImage!: HTMLImageElement;
    stoppedImage!: HTMLImageElement;
    idleImage!: HTMLImageElement;
    oosImage!: HTMLImageElement;

    canvas!: HTMLCanvasElement;

    location!: VisionMapLocation;
    position!: google.maps.LatLng;
    rotation!: number;
    label!: string;
    map!: VisionMap;
    indicator!: [string | null, string | null, string | null, string | null];

    colour: string;
    icon: string;
    id: string;

    last: AssetTelemetry | undefined;
    tail: RingBuffer<AssetTelemetry>;
    tailLine!: google.maps.Polyline;

    constructor(map: VisionMap, data: AssetTelemetry) {
        this.offImage = new Image();
        this.offImage.src = 'data:image/svg+xml,' + encodeURIComponent(SVGIcons.square('#888888', 12, 12));
        this.offImage.crossOrigin = 'Anonymous';

        this.movingImage = new Image();
        this.movingImage.src = 'data:image/svg+xml,' + encodeURIComponent(SVGIcons.right_arrow('#00FF00', 22, 18));
        this.movingImage.crossOrigin = 'Anonymous';

        this.idleImage = new Image();
        this.idleImage.src = 'data:image/svg+xml,' + encodeURIComponent(SVGIcons.idle('#0000FF', 20, 20));
        this.idleImage.crossOrigin = 'Anonymous';

        this.stoppedImage = new Image();
        this.stoppedImage.src = 'data:image/svg+xml,' + encodeURIComponent(SVGIcons.square('#FF0000', 12, 12));
        this.stoppedImage.crossOrigin = 'Anonymous';

        this.oosImage = new Image();
        this.oosImage.src = 'data:image/svg+xml,' + encodeURIComponent(SVGIcons.triangle('rgb(255, 145, 0)', 18, 18));
        this.oosImage.crossOrigin = 'Anonymous';

        this.highlight = false;
        this.colour = '#888888';
        this.icon = '';
        this.id = data.id;
        this.last = undefined;

        this.map = map;

        this.indicator = [null, null, null, null];
        this.tail = new RingBuffer<AssetTelemetry>(5);

        const polyOptions = {
            strokeOpacity: 1,
            strokeColor: '#0000FFCC',
            strokeWeight: 2,
            geodesic: true,
        };
        this.tailLine = new google.maps.Polyline(polyOptions);

        this.setIcon(data);
        this.setupCanvas(data);
    }

    public setPosition(lat: number, lon: number, heading: number) {
        this.position = new google.maps.LatLng(lat, lon);

        this.marker.setPosition(new google.maps.LatLng(lat, lon));
        this.rotation = heading;
        this.drawToCanvas();
    }

    private setupCanvas(config: AssetTelemetry): void {
        this.canvas = document.createElement('canvas');

        this.location = {
            latitude: config.position.coordinates[1],
            longitude: config.position.coordinates[0],
        };
        this.position = new google.maps.LatLng(this.location.latitude, this.location.longitude);
        this.canvas.width = 100;
        this.canvas.height = 100;

        this.label = config.name;

        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        this.context = this.canvas.getContext('2d')!;
        const textMetrics = this.context.measureText(this.label);
        this.width = textMetrics.width;

        this.image = new Image();
        this.image.src = 'data:image/svg+xml,' + encodeURIComponent(this.icon);

        this.image.crossOrigin = 'Anonymous';
        this.image.onload = () => {
            this.drawToCanvas();
            this.setupMarker();
        };
    }

    public drawToCanvas(): void {
        this.context.save();
        this.context.clearRect(0, 0, this.canvas.width, 80);
        this.context.restore();
        this.context.save();

        this.context.globalAlpha = 1.0;

        const flip = false;
        const rotation = this.rotation - 90;

        if (this.highlight == true) {
            this.context.beginPath();
            this.context.arc(50, 50, 16, 0, 2 * Math.PI);
            this.context.closePath();
            this.context.strokeStyle = 'red';
            this.context.stroke();
        }

        if (VisionApplication.getInstance().userService.icons == false) {
            if (this.indicator[VisionMapMarker.FRONT_ARC] != null) this.drawArc(this.rotation, VisionMapMarker.FRONT_ARC, this.indicator[VisionMapMarker.FRONT_ARC] + 'BB', flip);
            if (this.indicator[VisionMapMarker.BOTTOM_ARC] != null) this.drawArc(this.rotation, VisionMapMarker.BOTTOM_ARC, this.indicator[VisionMapMarker.BOTTOM_ARC] + 'BB', flip);
            if (this.indicator[VisionMapMarker.TOP_ARC] != null) this.drawArc(this.rotation, VisionMapMarker.TOP_ARC, this.indicator[VisionMapMarker.TOP_ARC] + 'BB', flip);
            if (this.indicator[VisionMapMarker.REAR_ARC] != null) this.drawArc(this.rotation, VisionMapMarker.REAR_ARC, this.indicator[VisionMapMarker.REAR_ARC] + 'BB', flip);
        }

        this.context.translate(50, 50);
        this.context.rotate((rotation * Math.PI) / 180);
        this.context.drawImage(this.image, -(this.image.width / 2), -(this.image.height / 2));
        this.context.restore();

        const asset = VisionApplication.getInstance().assetService.assetList.get(this.id);

        let backgroundColour = '#000000';
        let textColour = '#FFFFFF';
        if (asset) {
            backgroundColour = asset.config.labelColour != null ? asset.config.labelColour : backgroundColour;
            textColour = asset.config.textColour != null ? asset.config.textColour : textColour;
        }
        this.context.shadowColor = '#FFFFFF';
        this.context.fillStyle = '#000000';
        this.context.shadowBlur = 1;
        this.context.font = '12px Tahoma';
        const textMetrics = this.context.measureText(this.label);

        const ctx = this.context;
        // Define text properties
        const text = this.label;
        const fontSize = 12;
        const fontFamily = 'Arial';
        ctx.font = `${fontSize}px ${fontFamily}`;

        // Measure the text width to determine the rectangle size

        const textWidth = textMetrics.width;
        const textHeight = fontSize; // Approximate height based on font size

        // Define the position for the text and background
        const x = 100 - this.width;
        let y = 40; // y is the baseline, so adjust it if necessary
        if (VisionApplication.getInstance().userService.icons == true) {
            y = 80;
        }

        // Set the background color and draw the rectangle
        ctx.fillStyle = 'black';
        ctx.fillStyle = backgroundColour;
        ctx.fillRect(x - 20, y - textHeight, textWidth + 20, textHeight + 2);

        // Set the text color and draw the text
        ctx.fillStyle = 'white';
        ctx.fillStyle = textColour;
        ctx.fillText(text, x - 10, y);
    }

    edit(): void {
        alert('Edit');
    }

    setupMarker(): void {
        const shape = {
            coords: [0, 0, 10, 10],
            type: 'rect',
        };

        const squareIcon = {
            path: 'M -6 -6 L 6 -6 L 6 6 L -6 6 Z', // Path for a square (40x40)
            fillOpacity: 0.0, // Opacity of the fill
            strokeColor: 'black', // Border color of the square
            strokeWeight: 0, // Border thickness
            scale: 1.0, // Scale factor, 1 means 1 unit in the path = 1 pixel
            anchor: new google.maps.Point(0, 0), // Anchor at the center of the square
        };

        this.clickMarker = new google.maps.Marker({
            position: new google.maps.LatLng(this.location.latitude, this.location.longitude),
            map: this.map.map,
            clickable: true,
            icon: squareIcon,
            zIndex: 99,
        });

        this.marker = new google.maps.Marker({
            position: new google.maps.LatLng(this.location.latitude, this.location.longitude),
            shape: shape,
            anchorPoint: new google.maps.Point(0, -10),
            icon: {
                url: this.canvas.toDataURL(),
                anchor: new google.maps.Point(50, 50),
            },
            clickable: false,
        });
        this.clickMarker.addListener('dblclick', () => {
            VisionApplication.getInstance().pubsub.publish(PublishTopic.MARKER_CLICKED, this);
        });

        this.clickMarker.addListener('rightclick', (event: google.maps.MapMouseEvent) => {
            if (event.latLng != undefined) {
                const point = fromLatLngToPoint(event.latLng, this.map.map);
                this.map.activeMarkerData = this.id;
                this.map.showContextMenu(point.x, point.y);
            }
        });

        /*
        var contextMenuDiv = document.createElement("div");
        contextMenuDiv.id = "markerContextMenu";
        contextMenuDiv.style.position = "absolute";
        contextMenuDiv.style.backgroundColor = "white";
        contextMenuDiv.style.border = "1px solid #ccc";
        contextMenuDiv.style.padding = "8px";
        contextMenuDiv.style.boxShadow = "0 2px 6px rgba(0,0,0,0.3)";
        contextMenuDiv.style.zIndex = "10000";
        contextMenuDiv.style.display = "none";
        contextMenuDiv.innerHTML = `
            <div style="cursor:pointer" onclick='this.edit()'>Edit</div>
            <div style="cursor:pointer" onclick=>Track</div>
        `;
        document.body.appendChild(contextMenuDiv);


        this.clickMarker.addListener("rightclick", (event: google.maps.MapMouseEvent) =>{            
            const point = fromLatLngToPoint(event.latLng!, this.map.map);
            contextMenuDiv.style.left = `${point.x}px`;
            contextMenuDiv.style.top = `${point.y}px`;
            contextMenuDiv.style.display = "block";
        });

        this.map.map.addListener("click", () => {
            contextMenuDiv.style.display = "none";
        });
        */
        this.marker.setMap(this.map.map);

        if (this.marker) {
            const icon: google.maps.Icon = this.marker.getIcon() as google.maps.Icon;
            icon.url = this.canvas.toDataURL();
            this.marker.setIcon(icon);
        }

        VisionApplication.getInstance().pubsub.publish(PublishTopic.MARKER_CREATED, this);
    }

    drawArc(assetRotation: number, position: number, colour: string, flip: boolean) {
        // Defines the size of the circle
        const radius = 15;

        let start = 0;
        let end = 0;
        const rotation = assetRotation * 0.0174533;

        switch (position) {
            case VisionMapMarker.FRONT_ARC:
                start = 1.25 * Math.PI + rotation;
                end = 1.75 * Math.PI + rotation;
                break;
            case VisionMapMarker.TOP_ARC:
                if (flip == false) {
                    start = 0.75 * Math.PI + rotation;
                    end = 1.25 * Math.PI + rotation;
                } else {
                    start = 1.75 * Math.PI + rotation;
                    end = 0.25 * Math.PI + rotation;
                }
                break;
            case VisionMapMarker.REAR_ARC:
                start = 0.25 * Math.PI + rotation;
                end = 0.75 * Math.PI + rotation;
                break;
            case VisionMapMarker.BOTTOM_ARC:
                if (flip == false) {
                    start = 1.75 * Math.PI + rotation;
                    end = 0.25 * Math.PI + rotation;
                } else {
                    start = 0.75 * Math.PI + rotation;
                    end = 1.25 * Math.PI + rotation;
                }
                break;
        }

        this.context.beginPath();
        this.context.moveTo(50, 50);
        this.context.arc(50, 50, radius, start, end);
        this.context.closePath();
        this.context.fillStyle = colour;
        this.context.fill();
    }

    public updateIcon(): void {
        if (this.marker) {
            const icon: google.maps.Icon = this.marker.getIcon() as google.maps.Icon;
            icon.url = this.canvas.toDataURL();
            this.marker.setIcon(icon);
        }
    }

    private setIconGraphic(telemetry: AssetTelemetry, asset: AssetDTO) {
        let flip = false;
        if (telemetry.heading > 180) {
            flip = true;
        }
        this.icon = this.getIcon(asset?.config.icon, asset?.config.colour, flip);
    }

    private setIconSymbol(telemetry: AssetTelemetry, asset: AssetDTO) {
        const indicator = AssetService.findIndicatorForActivity('PLOW', asset.config);
        if (asset.config.usingBeacons == true) {
            if (telemetry.b <= 0) {
                if (this.last) {
                    switch (indicator) {
                        case 'FRONT':
                            this.last.front = null;
                            break;
                        case 'BACK':
                            this.last.back = null;
                            break;
                        case 'TOP':
                            this.last.top = null;
                            break;
                        case 'BOTTOM':
                            this.last.back = null;
                            break;
                    }
                }
            }
        }

        this.indicator = [null, null, null, null];
        if (this.last) {
            if (this.last.front) {
                this.indicator[VisionMapMarker.FRONT_ARC] = this.last.front;
            }
            if (this.last.back) {
                this.indicator[VisionMapMarker.REAR_ARC] = this.last.back;
            }
            if (this.last.top) {
                this.indicator[VisionMapMarker.TOP_ARC] = this.last.top;
            }
            if (this.last.bottom) {
                this.indicator[VisionMapMarker.BOTTOM_ARC] = this.last.bottom;
            }
        }

        if (asset.config.oos == true) {
            this.colour = 'rgb(255, 145, 0)';
            this.icon = SVGIcons.triangle(this.colour, 18, 18);
            telemetry.heading = 0;
            this.rotation = 90;
            this.image = this.oosImage;
        } else {
            this.icon = SVGIcons.square(this.colour, 12, 12);
            if (telemetry.state == MotionState.MOVING) {
                this.colour = '#00FF00';
                this.icon = SVGIcons.right_arrow(this.colour, 22, 18);
                this.image = this.movingImage;
                this.rotation = telemetry.heading;
            } else if (telemetry.state == MotionState.STOPPED) {
                this.colour = '#FF0000';
                telemetry.heading = 0;
                this.rotation = 0;
                this.icon = SVGIcons.square(this.colour, 12, 12);
                this.image = this.stoppedImage;
            } else if (telemetry.state == MotionState.IDLE) {
                this.colour = '#0000FF';
                this.icon = SVGIcons.idle(this.colour, 20, 20);
                telemetry.heading = 90;
                this.rotation = 90;
                this.image = this.idleImage;
            } else if (telemetry.state == MotionState.OFF) {
                telemetry.heading = 0;
                this.rotation = 0;
                this.image = this.offImage;
            }
        }
    }

    setIcon(item: AssetTelemetry) {
        this.rotation = item.heading;

        this.last = item;
        const asset = VisionApplication.getInstance().assetService.assetList.get(item.id);
        this.colour = '#888888';
        this.rotation = item.heading;

        if (asset) {
            if (VisionApplication.getInstance().userService.icons == true) {
                this.setIconGraphic(item, asset);
            } else {
                this.setIconSymbol(item, asset);
            }
        }
    }

    update(item: AssetTelemetry) {
        const updatePosition = new google.maps.LatLng(item.position.coordinates[1], item.position.coordinates[0]);

        if (google.maps.geometry.spherical.computeDistanceBetween(this.position, updatePosition) == 0) {
            return;
        }

        this.position = new google.maps.LatLng(item.position.coordinates[1], item.position.coordinates[0]);
        this.marker.setPosition(new google.maps.LatLng(item.position.coordinates[1], item.position.coordinates[0]));

        const last = this.tail.getLast();
        if (last) {
            const seen = last.lastSeen;
            if (seen) {
                if (seen != item.lastSeen) {
                    this.tail.add(item);
                }
            }
        } else {
            this.tail.add(item);
        }

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const coordinates = [] as any;

        this.tail.toArray().forEach((element) => {
            coordinates.push({
                lat: element.position.coordinates[1],
                lng: element.position.coordinates[0],
            });
        });

        const settings = VisionApplication.getInstance().userService.settings;
        if (settings != null && settings.asset_tail == false) {
            this.tailLine.setMap(null);
        } else {
            this.tailLine.setMap(this.map.map);
        }
        this.tailLine.setPath(coordinates);

        if (item.state == MotionState.OFF) {
            this.tailLine?.setMap(null);
        }

        this.setIcon(item);
        this.drawToCanvas();
        this.updateIcon();
    }

    private getIcon(icon: string | undefined, colour: string | undefined, flip: boolean): string {
        if (colour == undefined) {
            colour = 'black';
        }

        return SVGIcons.getIcon(icon as string, flip, colour);
    }
}
